
export default function BusinessImpact() {
  const revenueStreams = [
    {
      title: 'SaaS Subscriptions',
      description: 'Tiered pricing based on community size and features',
      icon: 'ri-money-dollar-box-line',
      percentage: '60%'
    },
    {
      title: 'Payment Processing',
      description: 'Transaction fees on dues and vendor payments',
      icon: 'ri-bank-card-line',
      percentage: '25%'
    },
    {
      title: 'Premium Add-ons',
      description: 'Advanced analytics, integrations, and white-labeling',
      icon: 'ri-vip-crown-line',
      percentage: '10%'
    },
    {
      title: 'Vendor Marketplace',
      description: 'Commission on vendor referrals and bookings',
      icon: 'ri-store-3-line',
      percentage: '5%'
    }
  ];

  const pricingTiers = [
    {
      name: 'Starter',
      price: '$99',
      period: '/month',
      units: 'Up to 50 units',
      features: ['Basic payments', 'Maintenance requests', 'Announcements', 'Document storage'],
      color: '#64748B',
      bgColor: '#F8FAFC'
    },
    {
      name: 'Professional',
      price: '$249',
      period: '/month',
      units: 'Up to 200 units',
      features: ['Everything in Starter', 'Violation management', 'Board portal', 'Custom branding'],
      color: '#1FA372',
      bgColor: '#F0FDF9',
      popular: true
    },
    {
      name: 'Enterprise',
      price: 'Custom',
      period: '',
      units: 'Unlimited units',
      features: ['Everything in Pro', 'API access', 'Dedicated support', 'Multi-community'],
      color: '#F97316',
      bgColor: '#FFF7ED'
    }
  ];

  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-2 bg-[#F97316]/10 text-[#F97316] rounded-full text-sm font-semibold mb-4 border border-[#F97316]/20">
            08 — Business
          </span>
          <h2 className="text-4xl lg:text-5xl font-bold text-[#1E293B] mb-6">
            Business Impact & Monetization
          </h2>
          <p className="text-[#64748B] text-lg max-w-3xl mx-auto">
            A sustainable business model that aligns user value with revenue growth.
          </p>
        </div>

        {/* Revenue Streams */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {revenueStreams.map((stream, index) => (
            <div 
              key={index}
              className="bg-[#FAFBFC] rounded-2xl p-6 border border-[#E2E8F0] text-center hover:shadow-lg transition-all hover:-translate-y-1"
            >
              <div className="w-14 h-14 flex items-center justify-center bg-[#1FA372]/10 rounded-2xl mx-auto mb-4 border border-[#1FA372]/20">
                <i className={`${stream.icon} text-[#1FA372] text-2xl`}></i>
              </div>
              <div className="text-3xl font-bold text-[#1FA372] mb-2">{stream.percentage}</div>
              <h3 className="text-[#1E293B] font-semibold mb-2">{stream.title}</h3>
              <p className="text-[#64748B] text-sm">{stream.description}</p>
            </div>
          ))}
        </div>

        {/* Pricing Strategy */}
        <div className="bg-[#FAFBFC] rounded-3xl p-8 border border-[#E2E8F0] mb-16 shadow-sm">
          <h3 className="text-2xl font-bold text-[#1E293B] mb-8 text-center">Pricing Strategy</h3>
          
          <div className="grid md:grid-cols-3 gap-6">
            {pricingTiers.map((tier, index) => (
              <div 
                key={index}
                className={`bg-white rounded-2xl p-6 border-2 ${tier.popular ? 'border-[#1FA372]' : 'border-[#E2E8F0]'} relative shadow-sm hover:shadow-lg transition-all`}
              >
                {tier.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="px-4 py-1 bg-[#1FA372] text-white text-xs font-semibold rounded-full whitespace-nowrap shadow-md">
                      Most Popular
                    </span>
                  </div>
                )}
                
                <div className="text-center mb-6">
                  <h4 className="text-[#1E293B] font-semibold mb-2">{tier.name}</h4>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-4xl font-bold" style={{ color: tier.color }}>{tier.price}</span>
                    <span className="text-[#64748B] text-sm">{tier.period}</span>
                  </div>
                  <p className="text-[#64748B] text-sm mt-2">{tier.units}</p>
                </div>

                <ul className="space-y-3">
                  {tier.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-3 text-[#64748B] text-sm">
                      <i className="ri-check-line text-[#1FA372]"></i>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Market Opportunity */}
        <div className="grid lg:grid-cols-2 gap-8">
          <div className="bg-gradient-to-br from-[#F0FDF9] to-white rounded-3xl p-8 border border-[#1FA372]/20 shadow-sm">
            <h3 className="text-2xl font-bold text-[#1E293B] mb-6 flex items-center gap-3">
              <i className="ri-global-line text-[#1FA372]"></i>
              Market Opportunity
            </h3>
            
            <div className="space-y-6">
              <div>
                <div className="flex justify-between mb-2">
                  <span className="text-[#64748B]">USA Market (TAM)</span>
                  <span className="text-[#1E293B] font-bold">$4.2B</span>
                </div>
                <div className="h-3 bg-[#E2E8F0] rounded-full overflow-hidden">
                  <div className="h-full bg-[#1FA372] rounded-full" style={{ width: '75%' }}></div>
                </div>
              </div>
              <div>
                <div className="flex justify-between mb-2">
                  <span className="text-[#64748B]">India Market (TAM)</span>
                  <span className="text-[#1E293B] font-bold">$1.8B</span>
                </div>
                <div className="h-3 bg-[#E2E8F0] rounded-full overflow-hidden">
                  <div className="h-full bg-[#F97316] rounded-full" style={{ width: '45%' }}></div>
                </div>
              </div>
              <div>
                <div className="flex justify-between mb-2">
                  <span className="text-[#64748B]">Annual Growth Rate</span>
                  <span className="text-[#1E293B] font-bold">12.5%</span>
                </div>
                <div className="h-3 bg-[#E2E8F0] rounded-full overflow-hidden">
                  <div className="h-full bg-[#3B82F6] rounded-full" style={{ width: '60%' }}></div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-[#FAFBFC] rounded-3xl p-8 border border-[#E2E8F0] shadow-sm">
            <h3 className="text-2xl font-bold text-[#1E293B] mb-6 flex items-center gap-3">
              <i className="ri-line-chart-line text-[#F97316]"></i>
              Growth Projections
            </h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white rounded-xl p-4 text-center border border-[#E2E8F0]">
                <div className="text-3xl font-bold text-[#1FA372] mb-1">500+</div>
                <div className="text-[#64748B] text-sm">Communities Y1</div>
              </div>
              <div className="bg-white rounded-xl p-4 text-center border border-[#E2E8F0]">
                <div className="text-3xl font-bold text-[#F97316] mb-1">2,500+</div>
                <div className="text-[#64748B] text-sm">Communities Y3</div>
              </div>
              <div className="bg-white rounded-xl p-4 text-center border border-[#E2E8F0]">
                <div className="text-3xl font-bold text-[#3B82F6] mb-1">$2.4M</div>
                <div className="text-[#64748B] text-sm">ARR Y1</div>
              </div>
              <div className="bg-white rounded-xl p-4 text-center border border-[#E2E8F0]">
                <div className="text-3xl font-bold text-[#8B5CF6] mb-1">$15M</div>
                <div className="text-[#64748B] text-sm">ARR Y3</div>
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-[#E2E8F0]">
              <div className="flex items-center justify-between">
                <span className="text-[#64748B]">Customer LTV</span>
                <span className="text-[#1E293B] font-bold">$8,400</span>
              </div>
              <div className="flex items-center justify-between mt-2">
                <span className="text-[#64748B]">CAC</span>
                <span className="text-[#1E293B] font-bold">$1,200</span>
              </div>
              <div className="flex items-center justify-between mt-2">
                <span className="text-[#64748B]">LTV:CAC Ratio</span>
                <span className="text-[#1FA372] font-bold">7:1</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
